import { useCallback, useEffect, useState } from "react";
import { Section } from "./kit";

interface Ready {
  status: "ready" | "degraded";
  version: string;
  checks: Record<string, "ok" | "unavailable">;
}

type Probe =
  | { kind: "loading" }
  | { kind: "ok"; data: Ready; latency: number; at: Date }
  | { kind: "down"; message: string; at: Date };

const REFRESH_MS = 15_000;

/** Reads the API's readiness probe now and then on a timer. */
function useProbe() {
  const [probe, setProbe] = useState<Probe>({ kind: "loading" });

  const check = useCallback(async () => {
    const started = performance.now();
    try {
      const res = await fetch("/api/v1/ready", { cache: "no-store" });
      const data = (await res.json()) as Ready;
      setProbe({ kind: "ok", data, latency: Math.round(performance.now() - started), at: new Date() });
    } catch {
      setProbe({ kind: "down", message: "The API did not answer.", at: new Date() });
    }
  }, []);

  useEffect(() => {
    // The first probe runs right away, then on a timer.
    const first = setTimeout(check, 0);
    const id = setInterval(check, REFRESH_MS);
    return () => {
      clearTimeout(first);
      clearInterval(id);
    };
  }, [check]);

  return probe;
}

const overallOf = (probe: Probe) => (probe.kind === "ok" ? probe.data.status : probe.kind === "down" ? "down" : "checking");

const OVERALL_LABEL: Record<string, string> = {
  ready: "All systems running",
  degraded: "Needs attention",
  down: "API not answering",
  checking: "Checking…",
};

/** The platform's state in a word, for the top bar. */
export function StatusPill() {
  const overall = overallOf(useProbe());
  return (
    <span className="status-pill" data-state={overall} role="status">
      <span className="ops-dot" />
      <span className="status-pill-label">{OVERALL_LABEL[overall]}</span>
    </span>
  );
}

/** Live health of the platform, read from the API's readiness probe. */
export function PlatformStatus() {
  const probe = useProbe();
  const services =
    probe.kind === "ok"
      ? [{ name: "api", state: "ok" as const }, ...Object.entries(probe.data.checks).map(([name, state]) => ({ name, state }))]
      : [{ name: "api", state: probe.kind === "down" ? ("unavailable" as const) : undefined }];

  const overall = overallOf(probe);

  return (
    <Section title="Platform status" className="status" aside={<span className="muted small">Every {REFRESH_MS / 1000} s</span>}>
      <p className="status-hero" data-state={overall}>
        <span className="ops-dot" />
        <span>
          {overall === "ready" && "All systems are running."}
          {overall === "degraded" && "Some systems need attention."}
          {overall === "down" && "The API is not answering."}
          {overall === "checking" && "Checking…"}
        </span>
      </p>

      <ul role="list" className="status-list">
        {services.map((s) => (
          <li key={s.name} data-state={s.state ?? "checking"}>
            <span className="ops-dot" />
            <span className="status-name">{s.name}</span>
            <span className="status-state">{s.state === "ok" ? "Operational" : s.state ? "Unavailable" : "Checking"}</span>
          </li>
        ))}
      </ul>

      <dl className="facts facts-compact">
        <div>
          <dt>Version</dt>
          <dd>{probe.kind === "ok" ? probe.data.version : "—"}</dd>
        </div>
        <div>
          <dt>Latency</dt>
          <dd>{probe.kind === "ok" ? `${probe.latency} ms` : "—"}</dd>
        </div>
        <div>
          <dt>Last checked</dt>
          <dd>{probe.kind === "loading" ? "—" : probe.at.toLocaleTimeString()}</dd>
        </div>
      </dl>
    </Section>
  );
}
